import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Trophy, Heart, Target, Globe, Shield } from "lucide-react";
import Navbar from "../../components/Navbar";

const values = [
  {
    icon: Heart,
    title: "Community First",
    body: "We build for the people who show up — every match, every meet-up, every milestone.",
    accent: "from-rose-500 to-orange-500",
  },
  {
    icon: Trophy,
    title: "Celebrate Wins",
    body: "From a club ladder to a national championship, every win deserves a spotlight.",
    accent: "from-amber-500 to-yellow-500",
  },
  {
    icon: Globe,
    title: "Open To All Sports",
    body: "Chess, cricket, tennis — and growing. One home for everything you love to play.",
    accent: "from-emerald-500 to-teal-500",
  },
  {
    icon: Shield,
    title: "Safe & Fair",
    body: "Moderation, verification, and clear rules keep play clean across every level.",
    accent: "from-sky-500 to-indigo-500",
  },
  {
    icon: Target,
    title: "Built On Outcomes",
    body: "Performance, records, and progress you can actually see and share.",
    accent: "from-fuchsia-500 to-purple-500",
  },
  {
    icon: Users,
    title: "Always Welcoming",
    body: "Beginner or pro — there's a seat at the table the moment you sign up.",
    accent: "from-cyan-500 to-blue-500",
  },
];

const stats = [
  { value: "3", label: "Sports today" },
  { value: "10+", label: "Cities live" },
  { value: "24/7", label: "Community" },
  { value: "100%", label: "Player-first" },
];

const AboutPage: React.FC = () => (
  <div className="min-h-screen bg-white text-gray-900">
    <Navbar />
    {/* ===== HERO ===== */}
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.18),_transparent_60%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20 sm:pt-32 sm:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="text-indigo-300 font-semibold tracking-wider text-xs sm:text-sm mb-4">
            ABOUT SPORTOFOLO
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Building India's most welcoming{" "}
            <span className="bg-gradient-to-r from-indigo-400 via-sky-400 to-emerald-400 bg-clip-text text-transparent">
              sports community
            </span>
            .
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-2xl">
            Sportofolo is where players find their people. We bring chess, cricket,
            tennis, and everything in between under one roof — so showing up to
            play has never been easier.
          </p>
        </motion.div>
      </div>
    </section>

    {/* ===== MISSION ===== */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
        <div>
          <p className="text-indigo-600 font-semibold tracking-wider text-xs sm:text-sm mb-4">
            OUR MISSION
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 leading-tight">
            Make every game easier to find, follow, and finish.
          </h2>
          <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
            Sport in India is everywhere — and almost nowhere online. Brackets
            live in WhatsApp groups, results live on paper, and the players who
            love the game most have nowhere to keep score. We're fixing that.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:gap-6">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-gray-100 bg-gradient-to-br from-gray-50 to-white p-6 text-center"
            >
              <div className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">
                {s.value}
              </div>
              <div className="text-gray-500 text-xs sm:text-sm mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ===== VALUES ===== */}
    <section className="bg-gradient-to-b from-gray-50 to-white py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
          <p className="text-indigo-600 font-semibold tracking-wider text-xs sm:text-sm mb-3">
            WHAT WE BELIEVE
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Six principles, one community.
          </h2>
          <p className="text-gray-600 leading-relaxed">
            These aren't slogans — they're decisions we make every release.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="group bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-xl hover:border-transparent transition-all"
              >
                <div
                  className={`w-12 h-12 rounded-xl bg-gradient-to-br ${v.accent} text-white flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
                >
                  <Icon size={22} />
                </div>
                <h3 className="text-lg font-bold mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ===== STORY ===== */}
    <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24">
      <p className="text-indigo-600 font-semibold tracking-wider text-xs sm:text-sm mb-3">
        THE STORY
      </p>
      <h2 className="text-3xl sm:text-4xl font-bold mb-8 leading-tight">
        Started by players, built for players.
      </h2>
      <div className="space-y-6 text-gray-700 leading-relaxed text-base sm:text-lg">
        <p>
          Sportofolo began with one frustration: there was no single place to
          follow the sports we actually played. Match results lived on WhatsApp.
          Tournament schedules lived on Instagram stories that disappeared.
          Achievements lived in nobody's memory but ours.
        </p>
        <p>
          We started by building the tool we wished existed — a community feed
          where matches, posts, articles, and recognition all live together.
          Today, players from across India use Sportofolo to share what they're
          training for and celebrate what they've already won.
        </p>
        <p>
          The roadmap is long. The mission is simple. <strong>Play. Connect.
          Compete.</strong>
        </p>
      </div>
    </section>

    {/* ===== CTA ===== */}
    <section className="bg-slate-950 text-white py-20 sm:py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">
          Join the community.
        </h2>
        <p className="text-gray-400 text-base sm:text-lg mb-8 max-w-xl mx-auto">
          It takes a minute. It lasts a season.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/signup"
            className="bg-white text-slate-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            Create Account
          </Link>
          <Link
            to="/"
            className="border border-white/30 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/10 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default AboutPage;
